import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { Button } from '../../components/ui';
import { track, EVENTS } from '../../utils/analytics';
import { buildSignupUrl } from '../../utils/signupUrl';

export default function ActivatePage() {
  const navigate = useNavigate();
  const { state, resetApi, resetQuizAnswers, resetExpressInput } = useApp();
  const { api, flow, quizAnswers, expressInput } = state;
  const isExpress = flow === 'express';

  useEffect(() => {
    if (api.status !== 'success' || !api.result) {
      navigate('/quiz', { replace: true });
    }
  }, [api.status, api.result, navigate]);

  useEffect(() => {
    track(EVENTS.ACTIVATE_PAGE_VIEWED, { flow });
  }, []);

  const signupUrl = useMemo(() => {
    return buildSignupUrl({
      quizAnswers,
      expressInput,
      flow,
      email: state.auth?.email || '',
    });
  }, [flow, quizAnswers, expressInput, state.auth?.email]);

  function handleStartOver() {
    track(EVENTS.CREATE_ANOTHER_PLAN_CLICKED, { flow });
    resetApi();
    resetQuizAnswers();
    resetExpressInput();
    navigate('/quiz');
  }

  if (!api.result) return null;

  return (
    <div className="activate-page">
      <div className="activate-page__container">
        {/* Top Section - Blueprint Ready */}
        <div className="activate-top">
          <div className="activate-top__icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="#d1fae5" />
              <path d="M15 24l6 6 12-12" stroke="#00a676" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="activate-top__headline">
            {isExpress ? 'Your Setup Is Ready!' : 'Your Blueprint Is Ready!'}
          </h1>
          <p className="activate-top__subtitle">
            {isExpress
              ? 'Check your email for your personalized financial setup'
              : 'Check your email for your personalized financial blueprint'}
          </p>
        </div>

        {/* Middle Section - Now What */}
        <div className="activate-middle">
          <h2 className="activate-middle__headline">The Difference Between a Plan and a Result</h2>
          <p className="activate-middle__subtitle">Choose how you want to move forward</p>
        </div>

        {/* Comparison Cards */}
        <div className="activate-cards">
          {/* DIY Card */}
          <div className="activate-card activate-card--diy">
            <h3 className="activate-card__title">Do It Yourself</h3>
            <p className="activate-card__desc">
              {isExpress
                ? 'Follow the setup manually. Move money between accounts, track expenses, and stay on top of deadlines yourself.'
                : 'Follow the blueprint manually. Track your own spending, move money between accounts, and stay on top of deadlines yourself.'}
            </p>
            <ul className="activate-card__list">
              <li className="activate-card__item">{isExpress ? 'Read through your setup' : 'Read through your blueprint'}</li>
              <li className="activate-card__item">Set up spreadsheets & reminders</li>
              <li className="activate-card__item">Spend 5+ hours/month managing it</li>
            </ul>
            <p className="activate-card__footer">{isExpress ? 'Your setup is waiting in your inbox' : 'Your blueprint is waiting in your inbox'}</p>
          </div>

          {/* Sequence Card */}
          <div className="activate-card activate-card--sequence">
            <span className="activate-card__badge">Recommended</span>
            <h3 className="activate-card__title">Your Blueprint, On Autopilot - Starting Today</h3>
            <p className="activate-card__desc">
              Every transfer, every month. Zero effort from your side.
            </p>
            <div className="activate-features">
              <div className="activate-feature">
                <span className="activate-feature__label">No More Mental Math</span>
                <span className="activate-feature__desc">Every dollar that comes in automatically flows to the right place. Bills, debt, savings, goals - all handled before you can spend it wrong.</span>
              </div>
              <div className="activate-feature">
                <span className="activate-feature__label">1:1 Expert Support</span>
                <span className="activate-feature__desc">Get personal guidance from a financial expert whenever you need it.</span>
              </div>
              <div className="activate-feature">
                <span className="activate-feature__label">Zero Risk to Try</span>
                <span className="activate-feature__desc">30-day money-back guarantee, no questions asked. If Sequence doesn't make your financial life easier, you pay nothing.</span>
              </div>
            </div>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => {
                track(EVENTS.SEQUENCE_SIGNUP_CLICKED, { flow });
                window.open(signupUrl, '_blank');
              }}
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              }
              iconPosition="right"
            >
              {isExpress ? 'Activate My Setup on Sequence' : 'Get Started with Sequence'}
            </Button>
          </div>
        </div>

        {/* Bottom Link */}
        <div className="activate-bottom">
          <button type="button" className="activate-bottom__link" onClick={handleStartOver}>
            {isExpress ? 'Create Another Setup' : 'Create Another Blueprint'}
          </button>
        </div>
      </div>
    </div>
  );
}
